import React, { useState, useEffect, useCallback } from "react";
import api from "http";
import { toast } from "react-toastify";
import { ErrorHandlingMessage } from "Utils/ErrorHandlingMessage";
import FormatDate from "Utils/FormatDate";
import NumberConversion from "Utils/NumberConversion";
import { isEmptyOrZero } from "Utils/Utils";
import { SAVED_AND_POSTED_SUCCESSFULLY } from "Common/ToastConstants";
import CustomInputWithDollar from "Common/CustomInputWithDollar";
import CustomButtonNew from "Common/CustomButtonNew/CustomButtonNew";
import CustomToggle from "Common/CustomToggle";
import Skeleton from "Components/skeleton/Skeleton";
import StatusPill from "Components/StatusPill/StatusPill";
import { Tooltip } from "react-tooltip";
import ReversalIcon from "SVGS/ReverseIcon";
import DeleteIcon from "SVGS/DeleteIcon";
import { swtichTab } from "Redux/TabsSlice/TabsSlice";
import { useDispatch, useSelector } from "react-redux";

const pmsApi = process.env.REACT_APP_TC_PMS;

const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(value || 0);
};
const buildServiceLineDataForVisit = (charges = []) => {
    const formattedData = {};

    charges.forEach((charge, index) => {
        let chargeKey = `${charge.visitChargeId}_${index}`;

        if (charge.isNewRectifiedRow && charge.uniqueRectificationId) {
            const rowType = charge.isNegativeRow ? "neg" : "pos";
            chargeKey = `${charge.visitChargeId}_rectified_${rowType}_${charge.uniqueRectificationId}`;
        }

        formattedData[chargeKey] = {
            ...charge,
            paidAmount: charge.thisPatientPaid || 0,
            writeOffAmount: charge.thisPatientDiscount || 0,
        };
    });

    return formattedData;
};
const renderSkeletonLoader = () => {
    return (
        <div className="bg-white border rounded-lg shadow-sm border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <div>
                        <Skeleton extraClass="h-6 w-48 mb-2" />
                        <Skeleton extraClass="h-4 w-64" />
                    </div>
                    <div className="flex items-center gap-3">
                        <Skeleton extraClass="h-10 w-32" />
                        <Skeleton extraClass="h-10 w-32" />
                    </div>
                </div>
            </div>

            <div className="px-6 py-6 space-y-4">
                {[1, 2].map((item) => (
                    <div key={item} className="overflow-hidden border rounded-lg bg-slate-50 border-slate-200">
                        {/* Claim Header */}
                        <div className="flex items-center gap-4 px-6 py-4 bg-white border-b border-slate-200">
                            <Skeleton extraClass="h-5 w-5 rounded" />
                            <div className="grid flex-1 grid-cols-10 gap-3">
                                <Skeleton extraClass="h-12 w-full" />
                                <Skeleton extraClass="h-12 w-full col-span-2" />
                                <Skeleton extraClass="h-12 w-full" />
                                <Skeleton extraClass="h-12 w-full" />
                                <Skeleton extraClass="h-12 w-full" />
                                <Skeleton extraClass="h-12 w-full" />
                                <Skeleton extraClass="h-12 w-full" />
                                <Skeleton extraClass="h-12 w-full" />
                                <Skeleton extraClass="h-12 w-full" />
                            </div>
                        </div>

                        {/* Service Lines Table Skeleton */}
                        <div className="px-4 py-4 bg-white">
                            <Skeleton extraClass="h-5 w-40 mb-3" />
                            <div className="overflow-x-auto border rounded border-slate-300">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50">
                                            <th className="w-12 px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-4 mx-auto" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-20" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-16" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-12" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-16" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-20" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-20" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-r border-slate-300">
                                                <Skeleton extraClass="h-4 w-20" />
                                            </th>
                                            <th className="px-3 py-2 border-b border-slate-300">
                                                <Skeleton extraClass="h-4 w-16" />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[1, 2, 3, 4].map((row) => (
                                            <tr key={row}>
                                                <td className="px-3 py-2 text-center border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-4 mx-auto" />
                                                </td>
                                                <td className="px-3 py-2 border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-16" />
                                                </td>
                                                <td className="px-3 py-2 border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-20" />
                                                </td>
                                                <td className="px-3 py-2 border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-16" />
                                                </td>
                                                <td className="px-3 py-2 border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-16" />
                                                </td>
                                                <td className="px-3 py-2 border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-16" />
                                                </td>
                                                <td className="px-3 py-2 border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-20" />
                                                </td>
                                                <td className="px-3 py-2 border-b border-r border-slate-200">
                                                    <Skeleton extraClass="h-4 w-20" />
                                                </td>
                                                <td className="px-3 py-2 text-center border-b border-slate-200">
                                                    <Skeleton extraClass="h-6 w-16 mx-auto" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
// 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
const getVisitStatusColor = (status) => {
    const colorMap = {
        Paid: "bg-green-100 text-green-700",
        Pending: "bg-amber-100 text-amber-700",
        Partial: "bg-blue-50 text-blue-700 border-blue-200",
    };
    return colorMap[status] || "bg-slate-100 text-slate-900 border-slate-200";
};

const PatientPaymentServiceLines = ({
    paymentModel,
    paymentId,
    setValidation,
    getPaymentMain,
    setState,
    state,
    selectedClaims = [],
}) => {
    const [visits, setVisits] = useState([]);
    const [expandedVisits, setExpandedVisits] = useState({});
    const [selectedServiceLines, setSelectedServiceLines] = useState({});
    const [currentAmounts, setCurrentAmounts] = useState({
        checkRemainingAmount: 0,
        allocatedAmount: 0,
        paidAmount: 0,
    })
    const [serviceLineData, setServiceLineData] = useState({});
    const [loading, setLoading] = useState(false);
    const [hideNegativePaid, setHideNegativePaid] = useState(false);
    const dispatch = useDispatch();
    const claimStatusCodes = useSelector((state) => state.dataSlice.ClaimStatusCodes);
    const gotoClaim = (row) => {
        dispatch(
            swtichTab({ parentName: "charges", subTabName: "claim", subTabData: { visitId: row.id, } })
        );
    };

    const fetchAllVisitsAndServiceLines = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get(
                `${pmsApi}/api/patientpayment/v2/GetDuePatientVisitPaymentsByPaymentMain`,
                {
                    params: {
                        patientId: paymentModel.patientId,
                        paymentMainId: paymentModel.id,
                        allPatientVisit: true,
                    },
                }
            );

            if (response.data && response.data.dataModel) {
                const visitsData = response.data.dataModel;
                setVisits(visitsData);

                // Initialize service line data with current values
                const initialData = {};
                visitsData.forEach((visit) => {
                    initialData[visit.id] = {};
                    visit.chargesInformation?.forEach((charge, index) => {
                        const ROW_KEY = `${charge.visitChargeId}_${index}`;
                        initialData[visit.id][ROW_KEY] = {
                            paidAmount: charge.thisPatientPaid || 0,
                            writeOffAmount: charge.thisPatientDiscount || 0,
                            ...charge,
                        };
                    });
                });
                setServiceLineData(initialData);
                setCurrentAmounts({
                    checkRemainingAmount: state.checkRemainingAmount,
                    allocatedAmount: state.allocatedAmount,
                    paidAmount: state.paidAmount
                })
                // Auto expand first visit
                if (visitsData.length > 0) {
                    setExpandedVisits({ [visitsData[0].id]: true });
                }
            }
        } catch (error) {
            ErrorHandlingMessage(error);
        } finally {
            setLoading(false);
        }
    }, [paymentModel?.patientId, paymentModel?.id]);

    useEffect(() => {

        if (paymentModel?.patientId && paymentModel?.id) {
            fetchAllVisitsAndServiceLines();

        }

    }, [paymentModel?.patientId, paymentModel?.id, fetchAllVisitsAndServiceLines]);

    // Handle when new claims are selected from the popup
    useEffect(() => {
        if (selectedClaims && selectedClaims.length > 0) {


            setVisits(prevVisits => {
                const newVisits = [];
                const newServiceLines = {};
                const newExpandedVisits = {};

                selectedClaims.forEach((selectedClaim) => {
                    // Check if claim already exists in visits
                    const claimExists = prevVisits.some(visit => visit.id === selectedClaim.id);



                    if (!claimExists) {
                        // Add the new claim to visits with visitPaymentId: 0 (indicates new linkage)
                        const newVisit = {
                            ...selectedClaim,
                            visitPaymentId: 0, // Important: 0 means new linkage during posting
                            isReversal: false,
                            isRectified: false,
                        };

                        newVisits.push(newVisit);

                        // Initialize service line data for the new claim
                        if (newVisit.chargesInformation) {
                            const claimServiceLineData = {};
                            newVisit.chargesInformation.forEach((charge, index) => {
                                const ROW_KEY = `${charge.visitChargeId}_${index}`;
                                claimServiceLineData[ROW_KEY] = {
                                    paidAmount: charge.thisPatientPaid || 0,
                                    writeOffAmount: charge.thisPatientDiscount || 0,
                                    ...charge,
                                };
                            });

                            newServiceLines[newVisit.id] = claimServiceLineData;
                        }

                        // Auto-expand the newly added claim
                        newExpandedVisits[newVisit.id] = true;
                    }
                });

                // Update service line data and expanded visits
                if (Object.keys(newServiceLines).length > 0) {
                    setServiceLineData(prev => ({
                        ...prev,
                        ...newServiceLines,
                    }));

                    setExpandedVisits(prev => ({
                        ...prev,
                        ...newExpandedVisits,
                    }));
                }



                // Return the updated visits array
                return newVisits.length > 0 ? [...prevVisits, ...newVisits] : prevVisits;
            });

            // Clear the selected claims after processing
            setState(prev => ({ ...prev, selectedClaims: [] }));
        }
    }, [selectedClaims, setState]);

    const toggleVisitExpansion = (visitId) => {
        setExpandedVisits((prev) => ({
            ...prev,
            [visitId]: !prev[visitId],
        }));
    };

    const handleServiceLineSelection = (visitId, chargeKey, isSelected) => {
        setSelectedServiceLines((prev) => {
            const updated = {
                ...prev,
                [visitId]: {
                    ...(prev[visitId] || {}),
                    [chargeKey]: isSelected,
                },
            };

            // Use setTimeout to ensure state updates are processed
            setTimeout(() => {
                const visit = visits.find((v) => v.id === parseInt(visitId));
                if (visit) {
                    // Get the charge data from serviceLineData first (most reliable)
                    const currentData = serviceLineData[visitId]?.[chargeKey];

                    // Find the charge in the visit's chargesInformation
                    let charge = null;

                    // For rectified rows, use the unique key to find the charge
                    if (chargeKey.includes('rectified')) {
                        // Find the charge by matching visitChargeId and uniqueRectificationId
                        const parts = chargeKey.split('_');
                        const visitChargeId = parseInt(parts[0]);
                        const rowType = parts[2]; // 'neg' or 'pos'
                        // The uniqueRectificationId is everything after the row type (parts[3] onwards, joined)
                        const uniqueRectificationId = parts.length > 3 ? parts.slice(3).join('_') : null;

                        charge = visit.chargesInformation?.find((c) => {
                            if (rowType === 'neg') {
                                return c.isNewRectifiedRow && c.isNegativeRow && c.visitChargeId === visitChargeId &&
                                    (!uniqueRectificationId || c.uniqueRectificationId === uniqueRectificationId);
                            } else {
                                return c.isNewRectifiedRow && !c.isNegativeRow && c.visitChargeId === visitChargeId &&
                                    (!uniqueRectificationId || c.uniqueRectificationId === uniqueRectificationId);
                            }
                        });
                    } else {
                        // For regular rows, try to find by visitChargeId first (more reliable than index)
                        const visitChargeId = parseInt(chargeKey.split('_')[0]);
                        charge = visit.chargesInformation?.find((c) =>
                            c.visitChargeId === visitChargeId &&
                            !c.isNewRectifiedRow &&
                            !c.isOldRectifiedRow
                        );

                        // If not found, try by index as fallback
                        if (!charge) {
                            const chargeIndex = parseInt(chargeKey.split('_').pop());
                            charge = visit.chargesInformation?.[chargeIndex];
                        }
                    }

                    // Use charge from serviceLineData if available, otherwise use the found charge
                    const chargeToUse = currentData || charge;

                    if (chargeToUse && chargeToUse.paymentStatus === "Pending" &&
                        !chargeToUse.isOldRectifiedRow && !chargeToUse.isNegativeRow) {

                        if (isSelected) {
                            // Auto-populate allocate amount with remaining balance when service line is selected
                            const remainingBalance = parseFloat(chargeToUse.cptBalance || 0);

                            // Only auto-populate if the field is empty or zero
                            if (remainingBalance > 0 && (!currentData?.paidAmount || parseFloat(currentData.paidAmount) === 0)) {
                                handleServiceLineChange(visitId, chargeKey, "paidAmount", remainingBalance.toFixed(2));
                            }
                        } else {
                            // Clear allocated amount and write-off amount when service line is unselected
                            // For pending rows, always reset to 0 when unselected
                            handleServiceLineChange(visitId, chargeKey, "paidAmount", "0");
                            handleServiceLineChange(visitId, chargeKey, "writeOffAmount", "0");
                        }
                    }
                }
            }, 0);

            return updated;
        });
    };

    const handleServiceLineChange = (visitId, chargeKey, field, value) => {
        setServiceLineData((prev) => ({
            ...prev,
            [visitId]: {
                ...prev[visitId],
                [chargeKey]: {
                    ...prev[visitId][chargeKey],
                    [field]: value,
                },
            },
        }));
    };

    const handleReversal = (visitId) => {
        const visit = visits.find((v) => v.id === visitId);
        if (!visit) return;

        const updatedCharges = [...visit.chargesInformation];
        const negativeRows = [];

        // Find all paid rows and create negative entries
        visit.chargesInformation?.forEach((charge, index) => {
            if (charge.paymentStatus === "Paid" && !charge.isOldRectifiedRow && !charge.isNegativeRow && !charge.isReversal) {
                // Mark the original charge as reversed
                updatedCharges[index] = {
                    ...charge,
                    isReversedRow: true,
                };

                const negativeRow = {
                    ...charge,
                    isNegativeRow: true,
                    visitPaymentId: charge.visitPaymentId,
                    isReversal: true,
                    cptCharges: charge.cptCharges * -1,
                    thisPatientPaid: charge.thisPatientPaid * -1,
                    thisPatientDiscount: charge.thisPatientDiscount * -1,
                    cptBalance: charge.cptBalance * -1,
                    cptPatientPaid: charge.cptPatientPaid * -1,
                    patientWriteOffAmount: charge.patientWriteOffAmount * -1,
                    paymentStatus: "Pending",
                };
                negativeRows.push(negativeRow);
            }
        });


        const finalCharges = [...updatedCharges, ...negativeRows];


        const updatedVisits = visits.map((v) =>
            v.id === visitId
                ? { ...v, chargesInformation: finalCharges, isReversal: true }
                : v
        );
        setVisits(updatedVisits);

        // Update service line data for negative rows
        const newServiceLineData = { ...serviceLineData[visitId] };
        const newSelectedServiceLines = { ...selectedServiceLines[visitId] };
        negativeRows.forEach((negRow, idx) => {
            const NEGATIVE_KEY = `${negRow.visitChargeId}_${updatedCharges.length + idx}`;
            newServiceLineData[NEGATIVE_KEY] = {
                ...negRow,
                paidAmount: negRow.thisPatientPaid,
                writeOffAmount: negRow.thisPatientDiscount,
            };
            // Auto-select reversal rows by default
            newSelectedServiceLines[NEGATIVE_KEY] = true;
        });

        setServiceLineData((prev) => ({
            ...prev,
            [visitId]: newServiceLineData,
        }));

        // Auto-select the reversal rows
        setSelectedServiceLines((prev) => ({
            ...prev,
            [visitId]: newSelectedServiceLines,
        }));
    };

    const handleCancelReversal = (visitId) => {
        const visit = visits.find((v) => v.id === visitId);
        if (!visit) return;

        const restoredCharges =
            (visit.chargesInformation || [])
                .filter((charge) => !(charge.isReversal && charge.isNegativeRow))
                .map((charge) => {
                    if (charge.isReversedRow) {
                        return {
                            ...charge,
                            isReversedRow: false,
                        };
                    }

                    return { ...charge };
                }) || [];

        const rebuiltServiceLineData = buildServiceLineDataForVisit(restoredCharges);

        setVisits((prev) =>
            prev.map((v) =>
                v.id === visitId
                    ? {
                        ...v,
                        chargesInformation: restoredCharges,
                        isReversal: false,
                    }
                    : v
            )
        );

        setServiceLineData((prev) => {
            return {
                ...prev,
                [visitId]: rebuiltServiceLineData,
            };
        });

        setSelectedServiceLines((prev) => {
            if (!prev[visitId]) {
                return prev;
            }

            const updatedSelections = { ...prev };
            delete updatedSelections[visitId];
            return updatedSelections;
        });
    };

    const handleDeleteClaim = (visitId) => {
        const updatedVisits = visits.filter((v) => v.id !== visitId);
        setVisits(updatedVisits);

        // Remove from service line data
        setServiceLineData((prev) => {
            const updated = { ...prev };
            delete updated[visitId];
            return updated;
        });

        // Remove from selected service lines
        setSelectedServiceLines((prev) => {
            const updated = { ...prev };
            delete updated[visitId];
            return updated;
        });
    };

    const handleRectify = (visitId, charge, chargeIndex) => {
        const visit = visits.find((v) => v.id === visitId);
        const updatedCharges = [...visit.chargesInformation];
        const originalCharge = updatedCharges[chargeIndex];

        // Generate a unique rectification ID for this specific rectification
        // This ensures uniqueness even when multiple rows are rectified
        const UNIQUE_RECTIFICATION_ID = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const rectifiedID = originalCharge.visitChargePaymentId;

        // Create negative row (before modifying original)
        const negativeRow = {
            ...originalCharge,
            isOldRectifiedRow: false,
            isNewRectifiedRow: true,
            isNegativeRow: true,
            rectifiedID: rectifiedID,
            uniqueRectificationId: UNIQUE_RECTIFICATION_ID, // Unique ID for this rectification pair
            cptCharges: originalCharge.cptCharges * -1,
            thisPatientPaid: originalCharge.thisPatientPaid * -1,
            thisPatientDiscount: originalCharge.thisPatientDiscount * -1,
        };

        // Create positive editable row (before modifying original)
        const positiveRow = {
            ...originalCharge,
            isOldRectifiedRow: false,
            isNewRectifiedRow: true,
            isNegativeRow: false,
            uniqueRectificationId: UNIQUE_RECTIFICATION_ID, // Same unique ID to link the pair
            thisPatientPaid: 0,
            thisPatientDiscount: 0,
            paymentStatus: "Pending",
        };

        // Now mark original as rectified (after creating new rows)
        originalCharge.isOldRectifiedRow = true;

        // Insert rows after original
        updatedCharges.splice(chargeIndex + 1, 0, negativeRow, positiveRow);

        // Update visits
        const updatedVisits = visits.map((v) =>
            v.id === visitId
                ? { ...v, chargesInformation: updatedCharges, isRectified: true }
                : v
        );
        setVisits(updatedVisits);

        // Update service line data
        // Use unique keys based on uniqueRectificationId instead of indices
        // This ensures uniqueness even when multiple rows are rectified
        const NEGATIVE_KEY = `${negativeRow.visitChargeId}_rectified_neg_${UNIQUE_RECTIFICATION_ID}`;
        const POSITIVE_KEY = `${positiveRow.visitChargeId}_rectified_pos_${UNIQUE_RECTIFICATION_ID}`;

        setServiceLineData((prev) => ({
            ...prev,
            [visitId]: {
                ...prev[visitId],
                [NEGATIVE_KEY]: {
                    ...negativeRow,
                    paidAmount: negativeRow.thisPatientPaid,
                    writeOffAmount: negativeRow.thisPatientDiscount,
                },
                [POSITIVE_KEY]: {
                    ...positiveRow,
                    paidAmount: 0,
                    writeOffAmount: 0,
                },
            },
        }));

        // Auto-select only the newly created negative and positive rectified rows
        // Clear any existing selections for this visit to ensure only the rectified rows are selected
        setSelectedServiceLines((prev) => {
            const updated = { ...prev };
            // Only select the two newly created rectified rows for this visit
            // Clear all other selections for this visit to avoid selecting any other pending rows
            updated[visitId] = {
                [NEGATIVE_KEY]: true,
                [POSITIVE_KEY]: true,
            };

            return updated;
        });
    };

    const handleCancelRectify = (visitId, charge, chargeIndex) => {
        const visit = visits.find((v) => v.id === visitId);
        if (!visit) return;

        const updatedCharges = [...visit.chargesInformation];

        // Use uniqueRectificationId to find the rectification pair
        // This charge could be either the positive or negative row
        const uniqueRectificationId = charge.uniqueRectificationId;

        if (!uniqueRectificationId) {
            // Fallback: try to find by visitChargeId if uniqueRectificationId is missing
            const visitChargeId = charge.visitChargeId;
            const negativeRow = updatedCharges.find(
                (c) =>
                    c.isNewRectifiedRow &&
                    c.isNegativeRow &&
                    c.visitChargeId === visitChargeId
            );
            if (!negativeRow?.uniqueRectificationId) return;

            // Use the negative row's uniqueRectificationId
            const matchingId = negativeRow.uniqueRectificationId;

            // Find all charges with this uniqueRectificationId
            const chargesToRemove = [];
            const keysToRemove = [];

            updatedCharges.forEach((c, idx) => {
                if (c.uniqueRectificationId === matchingId && c.isNewRectifiedRow) {
                    chargesToRemove.push(idx);
                    const rowType = c.isNegativeRow ? 'neg' : 'pos';
                    const ROW_KEY = `${c.visitChargeId}_rectified_${rowType}_${matchingId}`;
                    keysToRemove.push(ROW_KEY);
                }
            });

            // Find the original charge by matching rectifiedID and visitChargeId
            const rectifiedID = negativeRow?.rectifiedID;
            if (rectifiedID) {
                const originalCharge = updatedCharges.find(
                    (c) =>
                        c.visitChargePaymentId === rectifiedID &&
                        c.isOldRectifiedRow &&
                        c.visitChargeId === visitChargeId
                );

                if (originalCharge) {
                    originalCharge.isOldRectifiedRow = false;
                }
            }

            // Remove rows in reverse order to maintain correct indices
            chargesToRemove.sort((a, b) => b - a).forEach(idx => {
                updatedCharges.splice(idx, 1);
            });

            // Check if there are any other rectified rows left in the visit
            const hasOtherRectifiedRows = updatedCharges.some(c => c.isNewRectifiedRow || c.isOldRectifiedRow);

            // Update visits
            const updatedVisits = visits.map((v) =>
                v.id === visitId
                    ? {
                        ...v,
                        chargesInformation: updatedCharges,
                        isRectified: hasOtherRectifiedRows
                    }
                    : v
            );
            setVisits(updatedVisits);

            // Clean up service line data - remove keys for deleted rows
            setServiceLineData((prev) => {
                const updated = { ...prev[visitId] };
                keysToRemove.forEach(key => {
                    delete updated[key];
                });
                return {
                    ...prev,
                    [visitId]: updated,
                };
            });

            // Clean up selected service lines - remove keys for deleted rows
            setSelectedServiceLines((prev) => {
                const updated = { ...prev[visitId] } || {};
                keysToRemove.forEach(key => {
                    delete updated[key];
                });
                return {
                    ...prev,
                    [visitId]: updated,
                };
            });

            return;
        }

        // Find all charges with this uniqueRectificationId (both negative and positive rows)
        const chargesToRemove = [];
        const keysToRemove = [];
        let rectifiedID = null;

        updatedCharges.forEach((c, idx) => {
            if (c.uniqueRectificationId === uniqueRectificationId && c.isNewRectifiedRow) {
                chargesToRemove.push(idx);
                const rowType = c.isNegativeRow ? 'neg' : 'pos';
                const ROW_KEY = `${c.visitChargeId}_rectified_${rowType}_${uniqueRectificationId}`;
                keysToRemove.push(ROW_KEY);

                // Get rectifiedID from negative row
                if (c.isNegativeRow) {
                    rectifiedID = c.rectifiedID;
                }
            }
        });

        // Find the original charge by matching rectifiedID and visitChargeId
        if (rectifiedID) {
            const originalCharge = updatedCharges.find(
                (c) =>
                    c.visitChargePaymentId === rectifiedID &&
                    c.isOldRectifiedRow &&
                    c.visitChargeId === charge.visitChargeId
            );

            if (originalCharge) {
                originalCharge.isOldRectifiedRow = false;
            }
        }

        // Remove rows in reverse order to maintain correct indices
        chargesToRemove.sort((a, b) => b - a).forEach(idx => {
            updatedCharges.splice(idx, 1);
        });

        // Check if there are any other rectified rows left in the visit
        const hasOtherRectifiedRows = updatedCharges.some(c => c.isNewRectifiedRow || c.isOldRectifiedRow);

        // Update visits
        const updatedVisits = visits.map((v) =>
            v.id === visitId
                ? {
                    ...v,
                    chargesInformation: updatedCharges,
                    isRectified: hasOtherRectifiedRows
                }
                : v
        );
        setVisits(updatedVisits);

        // Clean up service line data - remove keys for deleted rows
        setServiceLineData((prev) => {
            const updated = { ...prev[visitId] };
            keysToRemove.forEach(key => {
                delete updated[key];
            });
            return {
                ...prev,
                [visitId]: updated,
            };
        });

        // Clean up selected service lines - remove keys for deleted rows
        setSelectedServiceLines((prev) => {
            const updated = { ...prev[visitId] } || {};
            keysToRemove.forEach(key => {
                delete updated[key];
            });
            return {
                ...prev,
                [visitId]: updated,
            };
        });
    };

    const calculateSelectedTotals = useCallback(() => {
        let totalPaid = 0;
        let totalWriteOff = 0;
        let totalBilled = 0;

        Object.keys(selectedServiceLines).forEach((visitId) => {
            Object.keys(selectedServiceLines[visitId]).forEach((chargeKey) => {
                if (selectedServiceLines[visitId][chargeKey]) {
                    const chargeData = serviceLineData[visitId]?.[chargeKey];
                    if (chargeData) {

                        totalPaid += parseFloat(chargeData.paidAmount || 0);
                        totalWriteOff += parseFloat(chargeData.writeOffAmount || 0);
                        totalBilled += parseFloat(chargeData.cptCharges || 0);
                    }
                }
            });
        });

        return { totalPaid, totalWriteOff, totalBilled };
    }, [selectedServiceLines, serviceLineData]);

    // Update parent state with calculated totals whenever selections or amounts change

    useEffect(() => {
        const totals = calculateSelectedTotals();
        const baseAllocatedAmount = parseFloat(currentAmounts?.allocatedAmount || 0);
        const totalPaid = parseFloat(totals.totalPaid || 0);
        const baseCheckRemaining = parseFloat(currentAmounts?.checkRemainingAmount || 0);

        const nextAllocatedAmount = parseFloat((baseAllocatedAmount + totalPaid).toFixed(2));
        const nextCheckRemaining = parseFloat((baseCheckRemaining - totalPaid).toFixed(2));

        setState((prev) => {
            const prevAllocatedAmount = parseFloat(prev?.allocatedAmount || 0);
            const prevCheckRemaining = parseFloat(prev?.checkRemainingAmount || 0);

            if (
                prevAllocatedAmount === nextAllocatedAmount &&
                prevCheckRemaining === nextCheckRemaining
            ) {
                return prev;
            }

            return {
                ...prev,
                allocatedAmount: nextAllocatedAmount,
                checkRemainingAmount: nextCheckRemaining,
            };
        });
    }, [
        calculateSelectedTotals,
        currentAmounts?.allocatedAmount,
        currentAmounts?.checkRemainingAmount,
        setState,
    ]);

    const hasSelectedServiceLines = () => {
        return Object.values(selectedServiceLines).some((visit) =>
            Object.values(visit).some((selected) => selected)
        );
    };

    const hasPendingServiceLines = () => {
        return visits.some((visit) => {
            return visit.chargesInformation?.some((charge, idx) => {
                const isPending = charge.paymentStatus === "Pending";
                const isEditable = isPending && !charge.isOldRectifiedRow && !charge.isNegativeRow;
                return isEditable;
            });
        });
    };

    const handleHideNegativeToggle = () => {
        setHideNegativePaid((prev) => !prev);
    };

    const handleAutoAllocate = () => {
        const updatedServiceLineData = { ...serviceLineData };
        const updatedSelectedServiceLines = { ...selectedServiceLines };

        // Get remaining amount available for allocation (based on checkRemainingAmount)
        let remainingBalance = parseFloat(state.checkRemainingAmount || 0);

        if (remainingBalance <= 0) {
            toast.error("No remaining amount available for allocation");
            return;
        }

        // Collect all pending, editable service lines from all visits
        const allPendingServiceLines = [];
        visits.forEach((visit) => {
            visit.chargesInformation?.forEach((charge, idx) => {
                const isPending = charge.paymentStatus === "Pending";
                const isEditable = isPending && !charge.isOldRectifiedRow && !charge.isNegativeRow;

                if (isEditable) {
                    const CHARGE_KEY = `${charge.visitChargeId}_${idx}`;
                    allPendingServiceLines.push({
                        visit,
                        charge,
                        chargeKey: CHARGE_KEY,
                        index: idx,
                    });
                }
            });
        });

        if (allPendingServiceLines.length === 0) {
            toast.error("No pending service lines available for allocation");
            return;
        }

        // Distribute payment amount from top to bottom across all pending service lines
        allPendingServiceLines.forEach(({ visit, charge, chargeKey, index }) => {
            // Stop if no balance left to allocate
            if (remainingBalance <= 0) {
                return;
            }

            // Initialize service line data for this visit if not exists
            if (!updatedServiceLineData[visit.id]) {
                updatedServiceLineData[visit.id] = {};
            }
            if (!updatedSelectedServiceLines[visit.id]) {
                updatedSelectedServiceLines[visit.id] = {};
            }

            // Get current service line data or initialize with charge data
            const currentData = updatedServiceLineData[visit.id][chargeKey] || {
                paidAmount: charge.thisPatientPaid || 0,
                writeOffAmount: charge.thisPatientDiscount || 0,
                ...charge,
            };

            // Get the service line's remaining balance (cptBalance)
            const serviceLineBalance = parseFloat(charge.cptBalance || 0).toFixed(2);

            // Only allocate if service line has a balance and we have remaining payment
            if (serviceLineBalance > 0 && remainingBalance > 0) {
                // Allocate up to the service line's balance or remaining payment amount, whichever is smaller
                const allocationAmount = Math.min(serviceLineBalance, remainingBalance).toFixed(2);

                // Update the service line data
                updatedServiceLineData[visit.id][chargeKey] = {
                    ...currentData,
                    paidAmount: allocationAmount,
                    writeOffAmount: currentData.writeOffAmount || 0,
                };

                // Auto-select this service line
                updatedSelectedServiceLines[visit.id][chargeKey] = true;

                // Reduce remaining balance
                remainingBalance -= parseFloat(allocationAmount).toFixed(2);
            }
        });

        // Update state
        setServiceLineData(updatedServiceLineData);
        setSelectedServiceLines(updatedSelectedServiceLines);

        toast.success("Payment allocated automatically across all pending service lines");
    };

    const handlePost = async () => {
        try {
            const validationError = setValidation();
            if (validationError) {
                return;
            }

            // Validate that at least one of allocated amount or writeoff has a value
            for (const visitId of Object.keys(selectedServiceLines)) {
                const visit = visits.find((v) => v.id === parseInt(visitId));
                if (!visit) continue;

                for (const chargeKey of Object.keys(selectedServiceLines[visitId])) {
                    if (selectedServiceLines[visitId][chargeKey]) {
                        const chargeData = serviceLineData[visitId]?.[chargeKey];
                        if (chargeData) {
                            // Skip validation for negative rows (reversals/rectifications)
                            if (chargeData.isNegativeRow || chargeData.isNewRectifiedRow) {
                                continue;
                            }

                            // Check if values are empty, null, undefined, or 0
                            const paidAmountValue = chargeData.paidAmount;
                            const writeOffAmountValue = chargeData.writeOffAmount;

                            const isPaidAmountEmpty = isEmptyOrZero(paidAmountValue);
                            const isWriteOffAmountEmpty = isEmptyOrZero(writeOffAmountValue);

                            // At least one must have a valid value
                            if (isPaidAmountEmpty && isWriteOffAmountEmpty) {
                                toast.error(`Cannot post: At least one of allocated amount or write-off must have a value for CPT Code ${chargeData.procCode || chargeData.visitChargeId} in claim #${visit.id}.`);
                                return;
                            }
                        }
                    }
                }
            }

            // Validate that total allocation does not exceed payment amount (remaining balance should not be negative)
            const totals = calculateSelectedTotals();

            const totalAllocated = Number(totals.totalPaid).toFixed(2) + Number(totals.totalWriteOff).toFixed(2);
            const paymentAmount = parseFloat(paymentModel?.paidAmount || 0);
            const remainingBalance = Number(paymentAmount).toFixed(2) - Number(totalAllocated).toFixed(2);


            if (remainingBalance < 0) {
                toast.error(`Cannot post: The total allocated amount ${formatCurrency(totalAllocated)} exceeds the payment amount ${formatCurrency(paymentAmount)}. Please adjust the allocation amounts.`);
                return;
            }

            // Validate newly inserted visits: allocated + writeoff should not exceed remaining balance
            const isNewlyInsertedVisit = (visit) => visit.visitPaymentId === 0 || visit.visitPaymentId === undefined || !visit.visitPaymentId;

            for (const visitId of Object.keys(selectedServiceLines)) {
                const visit = visits.find((v) => v.id === parseInt(visitId));
                if (!visit) continue;

                // Only validate for newly inserted visits
                if (isNewlyInsertedVisit(visit)) {
                    for (const chargeKey of Object.keys(selectedServiceLines[visitId])) {
                        if (selectedServiceLines[visitId][chargeKey]) {
                            const chargeData = serviceLineData[visitId]?.[chargeKey];

                            if (chargeData) {
                                // Skip validation for negative rows (reversals/rectifications)
                                if (chargeData.isNegativeRow) {
                                    continue;
                                }

                                // Find the actual charge from chargesInformation using the chargeKey
                                // chargeKey format: `${visitChargeId}_${idx}`
                                const chargeIndex = parseInt(chargeKey.split('_').pop());
                                const actualCharge = visit.chargesInformation?.[chargeIndex];

                                if (!actualCharge) {
                                    continue;
                                }

                                // Check if this is a positive rectified row - use original row's balance
                                // A rectified row has isNewRectifiedRow and is not negative
                                const isRectifiedRow =
                                    ((actualCharge.isNewRectifiedRow || chargeData.isNewRectifiedRow) === true) &&
                                    (actualCharge.isNegativeRow !== true && chargeData.isNegativeRow !== true);

                                if (isRectifiedRow) {
                                    // For rectified rows, find the original row and use its paid + discount as balance limit
                                    const visitChargeId = actualCharge.visitChargeId || chargeData.visitChargeId;
                                    // Negative row has rectifiedID - find it from the corresponding negative row
                                    const negativeRow = visit.chargesInformation?.find(
                                        (c) =>
                                            c.isNewRectifiedRow &&
                                            c.isNegativeRow &&
                                            c.visitChargeId === visitChargeId
                                    );
                                    const rectifiedID = negativeRow?.rectifiedID;

                                    // Find the original row - it should have the same visitChargeId and be marked as isOldRectifiedRow
                                    // We match by visitChargeId first (most reliable), then verify by rectifiedID if available
                                    const originalRow = visit.chargesInformation?.find(
                                        (c) =>
                                            c.isOldRectifiedRow &&
                                            c.visitChargeId === visitChargeId &&
                                            (!rectifiedID || c.visitChargePaymentId === rectifiedID)
                                    );

                                    if (originalRow) {
                                        const allocatedAmount = parseFloat(chargeData.paidAmount || 0);
                                        const writeOffAmount = parseFloat(chargeData.writeOffAmount || 0);
                                        const totalAllocated = allocatedAmount + writeOffAmount;
                                        // For rectified rows: original row's paid + discount + visit amountDue
                                        const originalRowBalance = parseFloat(originalRow.thisPatientPaid || 0) + parseFloat(originalRow.thisPatientDiscount || 0);
                                        const visitAmountDue = parseFloat(visit.amountDue || 0);
                                        const remainingBalance = originalRowBalance + visitAmountDue;
                                        const SERVICE_LINE_IDENTIFIER = `CPT Code ${originalRow.procCode || originalRow.visitChargeId}`;

                                        if (totalAllocated > remainingBalance) {
                                            toast.error(`Cannot post: The sum of allocated amount ${formatCurrency(allocatedAmount)} and write-off ${formatCurrency(writeOffAmount)} exceeds the remaining balance ${formatCurrency(remainingBalance)} for service line ${SERVICE_LINE_IDENTIFIER} in claim #${visit.id}.`);
                                            return;
                                        }
                                    }
                                    // If original row not found, skip validation (rectified rows are special cases)
                                    continue;
                                }

                                // For regular (non-rectified) rows, validate against remaining balance
                                const allocatedAmount = parseFloat(chargeData.paidAmount || 0);
                                const writeOffAmount = parseFloat(chargeData.writeOffAmount || 0);
                                const totalAllocated = allocatedAmount + writeOffAmount;
                                const remainingBalance = parseFloat(actualCharge.cptBalance || 0);
                                const SERVICE_LINE_IDENTIFIER = `CPT Code ${actualCharge.procCode || actualCharge.visitChargeId}`;

                                if (totalAllocated > remainingBalance) {
                                    toast.error(`Cannot post: The sum of allocated amount ${formatCurrency(allocatedAmount)} and write-off ${formatCurrency(writeOffAmount)} exceeds the remaining balance ${formatCurrency(remainingBalance)} for service line ${SERVICE_LINE_IDENTIFIER} in claim #${visit.id}.`);
                                    return;
                                }
                            }
                        }
                    }
                }
            }

            setState((prev) => ({ ...prev, isLoading: true }));

            const finalizedPayload = [];

            Object.keys(selectedServiceLines).forEach((visitId) => {
                const visit = visits.find((v) => v.id === parseInt(visitId));
                if (!visit) return;

                const visitSelectedCharges = [];
                const rectifiedRowsMap = {}; // Track rectified rows to ensure pairs are included

                // First pass: collect selected charges and track rectified rows
                Object.keys(selectedServiceLines[visitId]).forEach((chargeKey) => {
                    if (selectedServiceLines[visitId][chargeKey]) {
                        const chargeData = serviceLineData[visitId]?.[chargeKey];
                        if (chargeData) {
                            // Only negative rectified rows have rectifiedID - positive rows should not have it in payload
                            let rectifiedID = null;
                            if (chargeData.isNewRectifiedRow && chargeData.isNegativeRow) {
                                rectifiedID = chargeData.rectifiedID;
                            }

                            // For tracking purposes, get rectifiedID from negative row if this is a positive row
                            let trackingRectifiedID = rectifiedID;
                            if (chargeData.isNewRectifiedRow && !chargeData.isNegativeRow && !trackingRectifiedID) {
                                const negativeRow = visit.chargesInformation?.find(
                                    (c) =>
                                        c.isNewRectifiedRow &&
                                        c.isNegativeRow &&
                                        c.visitChargeId === chargeData.visitChargeId
                                );
                                trackingRectifiedID = negativeRow?.rectifiedID;
                            }

                            const chargePayload = {
                                coInsuranceAmount: chargeData.coInsuranceAmount || 0,
                                copayAmount: chargeData.copayAmount || 0,
                                deductibleAmount: chargeData.deductibleAmount || 0,
                                otherPR: chargeData.otherPR || 0,
                                visitPaymentId: chargeData.visitPaymentId || 0,
                                visitChargeId: chargeData.visitChargeId,
                                visitChargePaymentId: chargeData.visitChargePaymentId || 0,
                                procCode: chargeData.procCode,
                                dosFrom: chargeData.dosFrom,
                                dosTo: chargeData.dosTo,
                                writeOffAmount: parseFloat(chargeData.writeOffAmount || 0),
                                paidAmount: parseFloat(chargeData.paidAmount || 0),
                                isReversal: chargeData.isReversal || false,
                                isNewRectifiedRow: chargeData.isNewRectifiedRow || false,
                                rectifiedID: rectifiedID, // Only set for negative rows, null for positive rows
                                isNegativeRow: chargeData.isNegativeRow || false,
                            };
                            visitSelectedCharges.push(chargePayload);

                            // Track rectified rows by rectifiedID (using trackingRectifiedID for matching)
                            if (chargeData.isNewRectifiedRow && trackingRectifiedID) {
                                if (!rectifiedRowsMap[trackingRectifiedID]) {
                                    rectifiedRowsMap[trackingRectifiedID] = { negative: null, positive: null };
                                }
                                if (chargeData.isNegativeRow) {
                                    rectifiedRowsMap[trackingRectifiedID].negative = chargePayload;
                                } else {
                                    rectifiedRowsMap[trackingRectifiedID].positive = chargePayload;
                                }
                            }
                        }
                    }
                });

                // Second pass: ensure rectified row pairs are included
                Object.values(rectifiedRowsMap).forEach((pair) => {
                    // If positive row is selected but negative is not, add negative
                    if (pair.positive && !pair.negative) {
                        // Find the negative row from the visit's charges by matching uniqueRectificationId
                        // First, find the positive row to get its uniqueRectificationId
                        const positiveRow = visit.chargesInformation?.find(
                            (c) => c.isNewRectifiedRow &&
                                !c.isNegativeRow &&
                                c.visitChargeId === pair.positive.visitChargeId
                        );
                        const uniqueRectificationId = positiveRow?.uniqueRectificationId;

                        if (uniqueRectificationId) {
                            const negativeRow = visit.chargesInformation?.find(
                                (c) => c.isNewRectifiedRow &&
                                    c.isNegativeRow &&
                                    c.uniqueRectificationId === uniqueRectificationId
                            );

                            if (negativeRow) {
                                const NEGATIVE_KEY = `${negativeRow.visitChargeId}_rectified_neg_${uniqueRectificationId}`;
                                const negativeChargeData = serviceLineData[visitId]?.[NEGATIVE_KEY] || negativeRow;
                                visitSelectedCharges.push({
                                    visitChargeId: negativeChargeData.visitChargeId,
                                    visitChargePaymentId: negativeChargeData.visitChargePaymentId || 0,
                                    procCode: negativeChargeData.procCode,
                                    dosFrom: negativeChargeData.dosFrom,
                                    dosTo: negativeChargeData.dosTo,
                                    writeOffAmount: parseFloat(negativeChargeData.writeOffAmount || 0),
                                    paidAmount: parseFloat(negativeChargeData.paidAmount || 0),
                                    isReversal: negativeChargeData.isReversal || false,
                                    isNewRectifiedRow: true,
                                    rectifiedID: negativeChargeData.rectifiedID || negativeRow.rectifiedID || null, // Negative rows have rectifiedID
                                    isNegativeRow: true,
                                });
                            }
                        }
                    }
                    // If negative row is selected but positive is not, add positive
                    if (pair.negative && !pair.positive) {
                        // Find the positive row from the visit's charges by matching uniqueRectificationId
                        // First, find the negative row to get its uniqueRectificationId
                        const negativeRow = visit.chargesInformation?.find(
                            (c) => c.isNewRectifiedRow &&
                                c.isNegativeRow &&
                                c.visitChargeId === pair.negative.visitChargeId
                        );
                        const uniqueRectificationId = negativeRow?.uniqueRectificationId;

                        if (uniqueRectificationId) {
                            const positiveRow = visit.chargesInformation?.find(
                                (c) => c.isNewRectifiedRow &&
                                    !c.isNegativeRow &&
                                    c.uniqueRectificationId === uniqueRectificationId
                            );

                            if (positiveRow) {
                                const POSITIVE_KEY = `${positiveRow.visitChargeId}_rectified_pos_${uniqueRectificationId}`;
                                const positiveChargeData = serviceLineData[visitId]?.[POSITIVE_KEY] || positiveRow;
                                visitSelectedCharges.push({
                                    visitChargeId: positiveChargeData.visitChargeId,
                                    visitChargePaymentId: positiveChargeData.visitChargePaymentId || 0,
                                    procCode: positiveChargeData.procCode,
                                    dosFrom: positiveChargeData.dosFrom,
                                    dosTo: positiveChargeData.dosTo,
                                    writeOffAmount: parseFloat(positiveChargeData.writeOffAmount || 0),
                                    paidAmount: parseFloat(positiveChargeData.paidAmount || 0),
                                    isReversal: positiveChargeData.isReversal || false,
                                    isNewRectifiedRow: true,
                                    rectifiedID: null, // Positive rows should not have rectifiedID
                                    isNegativeRow: false,
                                });
                            }
                        }
                    }
                });

                if (visitSelectedCharges.length > 0) {
                    const amountPaid = visitSelectedCharges.reduce(
                        (sum, charge) => sum + charge.paidAmount + charge.writeOffAmount,
                        0
                    );

                    finalizedPayload.push({
                        ...paymentModel,
                        amountPaid,
                        visitPaymentId: visit.visitPaymentId || 0,
                        isReversal: visit.isReversal || false,
                        isRectified: visit.isRectified || false,
                        patientID: paymentModel.patientId,
                        visitId: visit.id,
                        visitChargePatientPosting: visitSelectedCharges,
                    });
                }
            });

            if (finalizedPayload.length === 0) return toast.error("No valid service lines to post");

            await api.post(
                `${pmsApi}/api/PatientPayment/v2/SaveAndPostVisitPatientPaymentV2`,
                finalizedPayload
            );

            toast.success(SAVED_AND_POSTED_SUCCESSFULLY);
            setSelectedServiceLines({});
            await fetchAllVisitsAndServiceLines();
            await getPaymentMain().then(data => {

                setCurrentAmounts({
                    checkRemainingAmount: data.checkRemainingAmount,
                    allocatedAmount: data.allocatedAmount,
                    paidAmount: data.paidAmount
                })
            })

        } catch (error) {
            ErrorHandlingMessage(error);
        } finally {

            setState((prev) => ({ ...prev, isLoading: false }));
        }
    };



    if (loading) {
        return renderSkeletonLoader();
    }
    const hasNegativePaidServiceLines = () => {
        return visits.some((visit) => {
            return visit.chargesInformation?.some((charge) => (charge.paymentStatus === "Paid" && charge.cptPatientPaid < 0) || charge.isNegativeRow);
        });
    };
    return (
        <div className="bg-white border rounded-lg shadow-sm border-slate-200">
            <Tooltip id="reverse_payment_tooltip" style={{ maxWidth: 400, zIndex: 12 }} />
            <Tooltip id="delete_claim_tooltip" style={{ maxWidth: 400, zIndex: 12 }} />
            <Tooltip id="auto_allocate_tooltip" style={{ maxWidth: 400, zIndex: 12 }} />
            <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900">Allocate Payment for {paymentModel.patientName}</h2>
                        <p className="mt-1 text-sm text-slate-600">
                            Choose which claims to apply this payment to. The payment must be fully allocated to service lines of the claims.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {hasNegativePaidServiceLines() && <CustomToggle
                            label="Hide Edits"
                            checked={hideNegativePaid}
                            onChange={handleHideNegativeToggle}
                        />
                        }
                        <div
                            data-tooltip-id="auto_allocate_tooltip"
                            data-tooltip-content={!hasPendingServiceLines() ? "There are no pending claims for auto-allocation" : "Automatically allocate payment across all pending service lines from top to bottom"}
                        >
                            <CustomButtonNew
                                variant="filled"
                                color="primary"
                                label="Auto Allocate"
                                onClick={handleAutoAllocate}
                                disabled={!hasPendingServiceLines()}
                            />
                        </div>
                        {hasSelectedServiceLines() && (
                            <CustomButtonNew
                                variant="filled"
                                color="primary"
                                label="Post"
                                onClick={handlePost}
                            />
                        )}
                        <CustomButtonNew
                            variant="filled"
                            color="primary"
                            label="Add Claims"
                            onClick={() => setState((prev) => ({ ...prev, addClaimPopup: true }))}
                        />
                    </div>
                </div>

                {/* {hasSelectedServiceLines() && (
                    <div className="px-6 py-4 mt-4 rounded-lg bg-slate-50">
                        <div className="flex items-center divide-x divide-slate-300">
                            <div className="flex-1 px-4">
                                <div className="mb-1 text-sm font-medium text-slate-500">Payment Amount</div>
                                <div className="text-lg text-slate-900">{formatCurrency(paymentModel.paidAmount || 0)}</div>
                            </div>
                            <div className="flex-1 px-4">
                                <div className="mb-1 text-sm font-medium text-slate-500">Amount Allocated</div>
                                <div className="text-lg text-slate-900">
                                    {formatCurrency(selectedTotals.totalPaid + selectedTotals.totalWriteOff)}
                                </div>
                            </div>
                            <div className="w-px h-12 bg-slate-300"></div>
                            <div className="flex-1 px-4">
                                <div className="mb-1 text-sm font-medium text-slate-500">Remaining</div>
                                <div className="text-lg font-semibold" style={{ color: '#654321' }}>
                                    {formatCurrency((paymentModel.paidAmount || 0) - (selectedTotals.totalPaid + selectedTotals.totalWriteOff))}
                                </div>
                            </div>
                        </div>
                    </div>
                )} */}
            </div>

            {visits.length === 0 ? (
                <div className="px-6 py-12 text-center">
                    <div className="text-sm text-slate-500">No claims found for this payment</div>
                </div>
            ) : (
                <div className="px-6 py-6 space-y-4">
                    {visits.map((visit) => {

                        const colorCode = claimStatusCodes?.find((sc) => sc.statusDescription === visit.claimStatusCode)?.colorCode || ''
                        const isExpanded = expandedVisits[visit.id];
                        const visitSelectedCount = Object.values(
                            selectedServiceLines[visit.id] || {}
                        ).filter(Boolean).length;
                        const hasPaidServiceLines = visit.chargesInformation?.some(charge => charge.paymentStatus === "Paid");

                        return (
                            <div
                                key={visit.id}
                                className="overflow-hidden border rounded-lg bg-slate-50 border-slate-200"
                            >
                                {/* Visit Header */}
                                <div className="flex items-center gap-4 px-6 py-4 border-b bg-gray-50 border-slate-200">
                                    <button
                                        onClick={() => toggleVisitExpansion(visit.id)}
                                        className="transition-colors text-slate-600 hover:text-slate-800"
                                    >
                                        {isExpanded ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        )}
                                    </button>


                                    <div className="grid w-full gap-3 text-sm grid-cols-20">
                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Claim #</div>
                                            <div onClick={() => gotoClaim(visit)} className="text-lg font-semibold cursor-pointer hover:text-primary text-slate-900">{visit.id}</div>
                                        </div>
                                        <div className="col-span-3">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Claim Status</div>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    data-tooltip-id="claimStatusTooltip"
                                                    data-tooltip-content={visit.longDescription}
                                                    className="inline-flex min-w-fit items-center px-2.5 py-1 border rounded text-sm"
                                                    style={{
                                                        borderColor: colorCode,
                                                        color: colorCode,
                                                    }}
                                                >
                                                    {visit.claimStatusCode}
                                                </span>   {visitSelectedCount > 0 && (
                                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold leading-none">
                                                        {visitSelectedCount}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Billed</div>
                                            <div className="text-base font-semibold text-slate-900">{NumberConversion(visit.totalAmount)}</div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Primary Paid</div>
                                            <div className="text-slate-900">{NumberConversion(visit.primaryPaid)}</div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Secondary Paid</div>
                                            <div className="text-slate-900">{NumberConversion(visit.secondaryPaid)}</div>
                                        </div>
                                        {/* <div>
                                            <div className="mb-1 text-sm font-medium text-slate-500">Allowed</div>
                                            <div className="text-slate-900">{NumberConversion(visit.totalAmount - visit.adjustment)}</div>
                                        </div> */}
                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Patient Paid</div>
                                            <div className="text-slate-900">{NumberConversion(visit.patientPaid)}</div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Adjustment</div>
                                            <div className="text-slate-900">{NumberConversion(visit.adjustment)}</div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Pt Write-Off</div>
                                            <div className="text-slate-900">{NumberConversion(visit.patientWriteOff)}</div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Balance</div>
                                            <div className="text-base font-semibold text-slate-900">{NumberConversion(visit.amountDue)}</div>
                                        </div>
                                        <div className="col-span-1">
                                            <div className="mb-1 text-sm font-medium text-slate-500">Actions</div>
                                            <div className="flex items-center justify-center">
                                                {(() => {
                                                    const hasPaidServiceLine = visit.chargesInformation?.every(charge => charge.paymentStatus === "Paid");
                                                    const hasRectifiedServiceLine = visit.chargesInformation?.some(charge => charge.rectifiedID || charge.isRectified);
                                                    const isDisabled = visit.isReversal || visit.isRectified || hasRectifiedServiceLine || !hasPaidServiceLine;
                                                    return (
                                                        <button
                                                            onClick={() => handleReversal(visit.id)}
                                                            disabled={isDisabled}
                                                            className={`p-2 rounded transition-colors ${isDisabled
                                                                ? 'text-slate-300 cursor-not-allowed opacity-50'
                                                                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                                                }`}
                                                            data-tooltip-id="reverse_payment_tooltip"
                                                            data-tooltip-content={
                                                                visit.isReversal
                                                                    ? "Reversal Already Applied"
                                                                    : visit.isRectified
                                                                        ? "A reversal cannot be processed, as the visit has already been rectified."
                                                                        : hasRectifiedServiceLine
                                                                            ? "A reversal cannot be processed, as the service line has already been rectified."
                                                                            : !hasPaidServiceLine
                                                                                ? "A reversal can only be applied to posted claims"
                                                                                : "Reverse Payment"
                                                            }
                                                        >
                                                            <ReversalIcon />
                                                        </button>
                                                    );
                                                })()}
                                                <button
                                                    onClick={() => handleDeleteClaim(visit.id)}
                                                    disabled={hasPaidServiceLines}
                                                    className={`p-2 rounded transition-colors ${hasPaidServiceLines
                                                        ? 'text-slate-300 cursor-not-allowed opacity-50'
                                                        : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                                                        }`}
                                                    data-tooltip-id="delete_claim_tooltip"
                                                    data-tooltip-content={hasPaidServiceLines ? "A claim with paid service lines cannot be deleted" : 'Delete Claim'}
                                                >
                                                    <DeleteIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* Service Lines Table */}
                                {isExpanded && (
                                    <div className="px-4 py-4 bg-white">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-sm font-semibold text-slate-700">Service Line Details</h3>
                                        </div>

                                        <div className="overflow-x-auto border rounded border-slate-300">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="bg-slate-50">
                                                        <th className="w-12 px-3 py-2 text-sm font-semibold tracking-wide text-center capitalize border-b border-r text-slate-700 border-slate-300">
                                                            <input
                                                                type="checkbox"
                                                                onChange={(e) => {
                                                                    const isChecked = e.target.checked;
                                                                    visit.chargesInformation?.forEach((charge, idx) => {
                                                                        // Select all editable rows (pending, not old rectified, not negative rows, not newly rectified)
                                                                        if (charge.paymentStatus === "Pending" && !charge.isOldRectifiedRow && !charge.isNegativeRow && !charge.isNewRectifiedRow) {
                                                                            const ROW_KEY = `${charge.visitChargeId}_${idx}`;
                                                                            handleServiceLineSelection(visit.id, ROW_KEY, isChecked);
                                                                        }
                                                                    });
                                                                }}
                                                                className="w-4 h-4 text-blue-600 rounded cursor-pointer border-slate-300 focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </th>
                                                        <th className="text-left py-2 px-3 font-semibold text-slate-700 min-w-[80px] text-sm capitalize tracking-wide border-r border-b w-28 border-slate-300">Charge #</th>
                                                        <th className="px-3 py-2 text-sm font-semibold tracking-wide text-left capitalize border-b border-r text-slate-700 border-slate-300">DOS</th>
                                                        <th className="px-3 py-2 text-sm font-semibold tracking-wide text-left capitalize border-b border-r text-slate-700 border-slate-300">CPT</th>
                                                        <th className="px-3 py-2 text-sm font-semibold tracking-wide text-right capitalize border-b border-r text-slate-700 border-slate-300">Billed</th>
                                                        <th className="px-3 py-2 text-sm font-semibold tracking-wide text-right capitalize border-b border-r text-slate-700 border-slate-300">Deductible</th>
                                                        <th className="px-3 py-2 text-sm font-semibold tracking-wide text-right capitalize border-b border-r text-slate-700 border-slate-300">Copay</th>
                                                        <th className="text-right py-2 px-3 font-semibold text-slate-700 text-sm min-w-[80px] capitalize tracking-wide border-r border-b border-slate-300">Co-Ins</th>
                                                        <th className="text-right py-2 px-3 font-semibold text-slate-700 text-sm  min-w-[80px] tracking-wide border-r border-b border-slate-300">Other PR</th>

                                                        <th className="text-right py-2 px-3 font-semibold text-slate-700 text-sm capitalize min-w-[80px] tracking-wide border-r border-b border-slate-300">pt paid</th>
                                                        <th className="text-right py-2 px-3 font-semibold text-slate-700 text-sm capitalize min-w-[130px] tracking-wide border-r border-b border-slate-300">Remaining Balance</th>
                                                        <th className="px-3 py-2 text-sm font-semibold tracking-wide text-right capitalize border-b border-r text-slate-700 border-slate-300">Allocate</th>
                                                        <th className="text-right py-2 px-3 font-semibold text-slate-700 text-sm capitalize min-w-[100px] tracking-wide border-r border-b border-slate-300">Pt Write-Off</th>
                                                        <th className="px-3 py-2 text-sm font-semibold tracking-wide text-center capitalize border-b text-slate-700 border-slate-300">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visit.chargesInformation
                                                        ?.map((charge, originalIdx) => ({ charge, originalIdx }))
                                                        .sort((a, b) => {
                                                            // Put Pending items last
                                                            const aIsPending = a.charge.paymentStatus === "Pending" && !a.charge.isNewRectifiedRow && !a.charge.isReversal;
                                                            const bIsPending = b.charge.paymentStatus === "Pending" && !b.charge.isNewRectifiedRow && !b.charge.isReversal;
                                                            if (aIsPending && !bIsPending) return 1;
                                                            if (!aIsPending && bIsPending) return -1;
                                                            return 0; // Maintain original order for same status
                                                        })
                                                        .map(({ charge, originalIdx }, idx) => {
                                                            // Use unique keys for rectified rows to avoid conflicts

                                                            let chargeKey;
                                                            if (charge.isNewRectifiedRow) {
                                                                // Rectified rows use unique keys based on uniqueRectificationId
                                                                // This ensures uniqueness even when multiple rows are rectified
                                                                const uniqueRectificationId = charge.uniqueRectificationId;
                                                                if (uniqueRectificationId) {
                                                                    const rowType = charge.isNegativeRow ? 'neg' : 'pos';
                                                                    chargeKey = `${charge.visitChargeId}_rectified_${rowType}_${uniqueRectificationId}`;
                                                                } else {
                                                                    // Fallback: try to find uniqueRectificationId from corresponding negative row
                                                                    const negativeRow = visit.chargesInformation?.find(
                                                                        (c) => c.isNewRectifiedRow &&
                                                                            c.isNegativeRow &&
                                                                            c.visitChargeId === charge.visitChargeId
                                                                    );
                                                                    if (negativeRow?.uniqueRectificationId) {
                                                                        const rowType = charge.isNegativeRow ? 'neg' : 'pos';
                                                                        chargeKey = `${charge.visitChargeId}_rectified_${rowType}_${negativeRow.uniqueRectificationId}`;
                                                                    } else {
                                                                        // Last resort: use standard key if uniqueRectificationId not found
                                                                        chargeKey = `${charge.visitChargeId}_${originalIdx}`;
                                                                    }
                                                                }
                                                            } else {
                                                                // Regular rows use the standard key format
                                                                chargeKey = `${charge.visitChargeId}_${originalIdx}`;
                                                            }
                                                            const isSelected = selectedServiceLines[visit.id]?.[chargeKey] || false;
                                                            const chargeData = serviceLineData[visit.id]?.[chargeKey] || charge;
                                                            const paidAmountForFilter = chargeData?.paidAmount ?? charge.thisPatientPaid ?? 0;
                                                            const normalizedPaidAmount = Number.parseFloat(paidAmountForFilter) || 0;
                                                            if (hideNegativePaid && normalizedPaidAmount < 0) {
                                                                return null;
                                                            }
                                                            const isPending = charge.paymentStatus === "Pending";
                                                            const isPaid = charge.paymentStatus === "Paid";
                                                            // Editable inputs: pending, not old rectified, and not a reversal negative row (includes positive rectified rows)
                                                            const isEditable = isPending && !charge.isOldRectifiedRow && !charge.isNegativeRow;
                                                            // Reversal rows: show checkbox but disabled
                                                            const isReversalRow = charge.isReversal && charge.isNegativeRow;
                                                            // Negative rectified rows: show checkbox but disabled
                                                            const isNegativeRectifiedRow = charge.isNewRectifiedRow && charge.isNegativeRow && !charge.isReversal;
                                                            // Positive rectified rows: show checkbox but disabled
                                                            const isPositiveRectifiedRow = charge.isNewRectifiedRow && !charge.isNegativeRow;
                                                            const showCheckbox = isEditable || isReversalRow || isNegativeRectifiedRow || isPositiveRectifiedRow;
                                                            const isCheckboxDisabled = isReversalRow || isNegativeRectifiedRow || isPositiveRectifiedRow;
                                                            //has reversal in any service line
                                                            const hasReversal = visit.chargesInformation?.some(charge => charge.isReversal);
                                                            return (
                                                                <tr
                                                                    key={chargeKey}
                                                                    className={`hover:bg-slate-50 ${charge.isNegativeRow || charge.thisPatientPaid < 0 ? "bg-red-50" : ""}`}
                                                                >
                                                                    <td className="px-3 py-2 text-center border-b border-r border-slate-200">
                                                                        {showCheckbox ? (
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                onChange={(e) => handleServiceLineSelection(visit.id, chargeKey, e.target.checked)}
                                                                                disabled={isCheckboxDisabled}
                                                                                className={`w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 ${isCheckboxDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                                                            />
                                                                        ) : (
                                                                            <span className="inline-block w-4 h-4"></span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm border-b border-r text-slate-900 border-slate-200">
                                                                        <span className="font-bold">{charge.visitChargeId}</span>
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm border-b border-r text-slate-900 border-slate-200">
                                                                        {FormatDate(charge.dosFrom, "mm/dd/yyyy")}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm border-b border-r text-slate-900 border-slate-200">
                                                                        {charge.procCode}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-right border-b border-r text-slate-900 border-slate-200">
                                                                        {formatCurrency(charge.cptCharges)}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-right border-b border-r text-slate-900 border-slate-200">
                                                                        {formatCurrency(charge.deductibleAmount)}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-right border-b border-r text-slate-900 border-slate-200">
                                                                        {formatCurrency(charge.copayAmount)}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-right border-b border-r text-slate-900 border-slate-200">
                                                                        {formatCurrency(charge.coInsuranceAmount)}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-right border-b border-r text-slate-900 border-slate-200">
                                                                        {formatCurrency(charge.otherPR)}
                                                                    </td>

                                                                    <td className="px-3 py-2 text-sm text-right border-b border-r text-slate-900 border-slate-200">
                                                                        {formatCurrency(charge.cptPatientPaid)}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-right border-b border-r text-slate-900 border-slate-200">
                                                                        {formatCurrency(charge.cptBalance)}
                                                                    </td>
                                                                    <td className="px-3 py-2 border-b border-r border-slate-200">
                                                                        {isEditable ? (
                                                                            <CustomInputWithDollar
                                                                                maxFloat={1}
                                                                                placeHolder="0.00"
                                                                                isFloat
                                                                                isNumeric
                                                                                allowNegative={charge.isNegativeRow}
                                                                                name="paidAmount"
                                                                                value={chargeData.paidAmount}
                                                                                onChange={(e) => handleServiceLineChange(visit.id, chargeKey, "paidAmount", e.target.value)}
                                                                            />
                                                                        ) : (
                                                                            <div className="text-sm text-right text-slate-900">
                                                                                {formatCurrency(charge.isNegativeRow ? charge.thisPatientPaid : charge.cptPatientPaid)}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-3 py-2 border-b border-r border-slate-200">
                                                                        {isEditable ? (
                                                                            <CustomInputWithDollar
                                                                                maxFloat={1}
                                                                                placeHolder="0.00"
                                                                                isFloat
                                                                                isNumeric
                                                                                allowNegative={charge.isNegativeRow}
                                                                                name="writeOffAmount"
                                                                                value={chargeData.writeOffAmount}
                                                                                onChange={(e) => handleServiceLineChange(visit.id, chargeKey, "writeOffAmount", e.target.value)}
                                                                            />
                                                                        ) : (
                                                                            <div className="text-sm text-right text-slate-900">
                                                                                {formatCurrency(charge.isNegativeRow ? charge.thisPatientDiscount : charge.patientWriteOffAmount)}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center border-b border-slate-200">
                                                                        {isPaid && !charge.isRectified && !hasReversal && !charge.isNewRectifiedRow && !charge.isOldRectifiedRow && !charge.isReversal && !charge.isReversedRow && !visit.isReversal ? (
                                                                            <button
                                                                                onClick={() => handleRectify(visit.id, charge, originalIdx)}
                                                                                disabled={visit.isReversal}
                                                                                className={`px-3 py-1 text-sm font-medium border border-slate-300 rounded transition-colors ${visit.isReversal
                                                                                    ? 'text-slate-300 bg-slate-100 cursor-not-allowed opacity-50'
                                                                                    : 'text-slate-700 bg-white hover:bg-slate-50'
                                                                                    }`}
                                                                                title={visit.isReversal ? "Cannot Rectify - Reversal Applied" : "Rectify"}
                                                                            >
                                                                                Rectify
                                                                            </button>
                                                                        ) : charge.isNewRectifiedRow && !charge.isNegativeRow ? (
                                                                            <button
                                                                                onClick={() => handleCancelRectify(visit.id, charge, originalIdx)}
                                                                                className="px-3 py-1 text-sm font-medium text-red-700 transition-colors bg-white border border-red-300 rounded hover:bg-red-50"
                                                                                title="Cancel Rectify"
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        ) : charge.rectifiedID || (charge.isRectified && charge.visitChargePaymentId) ? (
                                                                            <StatusPill variant="rectified" label="Rectified" />
                                                                        ) : charge.isNegativeRow && !charge.rectifiedID ? (
                                                                            charge.isReversal ? (
                                                                                <button
                                                                                    onClick={() => handleCancelReversal(visit.id)}
                                                                                    className="px-3 py-1 text-sm font-medium text-red-700 transition-colors bg-white border border-red-300 rounded hover:bg-red-50"
                                                                                    title="Cancel Reversal"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            ) : (
                                                                                <StatusPill variant="reversed" label="Reversal" />
                                                                            )
                                                                        ) : (charge.isReversedRow || (charge.isReversal && charge.cptPatientPaid < 0)) ? (
                                                                            <StatusPill variant="reversal" label="Reversed" />
                                                                        ) : null}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PatientPaymentServiceLines;
